import { Image, View, type ImageSourcePropType } from "react-native";
import { Row, colors } from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { useCycles } from "@src/packages/cycles/hooks/useCycles";
import { useFrozenSpecimens } from "@src/packages/care/cryovault/useSpecimens";
import { useDocuments } from "@src/packages/documents/hooks/useDocuments";
import { EMBER } from "../../../../onboarding/theme";
import { GroupedCard } from "../components";
import { ICONS } from "../icons";
import type { CareStackParamList } from "../../../types";

/**
 * This section is an index into the places where care data is actually
 * maintained. It deliberately does not duplicate onboarding answers,
 * connections, or a second clinic-history model.
 */
export type MedicalFertilityDestination =
  | { screen: "MedicalHistory" }
  | { screen: "FertilityTreatmentsFlow" }
  | { screen: "CryoVault" }
  | {
      screen: "Documents";
      params: NonNullable<CareStackParamList["Documents"]>;
    }
  | { screen: "AllergiesFlow" };

type Props = {
  onNavigate: (destination: MedicalFertilityDestination) => void;
};

type PatientWithPriorTreatmentAnswer = {
  fertilityHistory?: {
    hasPriorFertilityTreatments?: boolean | null;
  } | null;
};

const HISTORY_ILLUSTRATION =
  require("@src/apps/moon/assets/images/stethoscope-copper.png") as number;
const CRYO_ILLUSTRATION =
  require("@src/apps/moon/assets/images/cryo-vault-fridge.png") as number;
const ILLUSTRATION_SIZE = 28;

function CareIllustration({ source }: { source: ImageSourcePropType }) {
  return (
    <Image
      source={source}
      resizeMode="contain"
      style={{ width: ILLUSTRATION_SIZE, height: ILLUSTRATION_SIZE }}
    />
  );
}

function CareRow({
  source,
  title,
  subtitle,
  divider,
  onPress,
}: {
  source: ImageSourcePropType;
  title: string;
  subtitle: string;
  divider?: boolean;
  onPress: () => void;
}) {
  return (
    <Row
      surface="plain"
      leading={<CareIllustration source={source} />}
      leadingSize={ILLUSTRATION_SIZE}
      title={title}
      subtitle={subtitle}
      onPress={onPress}
      action="care_row"
      gap={14}
      paddingHorizontal={16}
      paddingVertical={12}
      borderRadius={0}
      titleStyle={{
        fontSize: 15,
        lineHeight: 20,
        fontWeight: "500",
        color: EMBER.colors.text,
      }}
      subtitleStyle={{
        fontSize: 12,
        lineHeight: 16,
        color: EMBER.colors.textLight,
      }}
      style={{
        borderRadius: 0,
        borderTopWidth: divider ? 1 : 0,
        borderTopColor: colors.border,
      }}
    />
  );
}

export default function CareContext({ onNavigate }: Props) {
  const t = useTranslations();
  const { data: patient, isLoading: patientLoading } = usePatient();
  const { data: cycles, isLoading: cyclesLoading } = useCycles();
  const { specimens, isLoading: specimensLoading } = useFrozenSpecimens();
  const { data: documents, isLoading: documentsLoading } = useDocuments();

  const pastTreatmentCount = (cycles ?? []).filter(
    (cycle) => cycle.status === "closed",
  ).length;
  const priorTreatmentAnswer = (
    patient as (typeof patient & PatientWithPriorTreatmentAnswer) | undefined
  )?.fertilityHistory?.hasPriorFertilityTreatments;
  const pastTreatmentLabel =
    cyclesLoading || patientLoading
      ? t("you.care_context.loading")
      : pastTreatmentCount === 1
        ? t("you.care_context.past_treatment_one")
        : pastTreatmentCount > 1
          ? t("you.care_context.past_treatment_other", {
              params: { count: String(pastTreatmentCount) },
            })
          : priorTreatmentAnswer === true
            ? t("you.care_context.past_treatment_reported")
            : t("you.care_context.past_treatment_none");

  const cryoCount = specimens.length;
  const cryoLabel = specimensLoading
    ? t("you.care_context.loading")
    : cryoCount === 0
      ? t("you.care_context.cryo_inventory_empty")
      : cryoCount === 1
        ? t("you.care_context.cryo_inventory_one")
        : t("you.care_context.cryo_inventory_other", {
            params: { count: String(cryoCount) },
          });

  const recordsCount = documents?.length ?? 0;
  const recordsLabel = documentsLoading
    ? t("you.care_context.loading")
    : recordsCount === 0
      ? t("you.care_context.records_empty")
      : recordsCount === 1
        ? t("you.care_context.records_one")
        : t("you.care_context.records_other", {
            params: { count: String(recordsCount) },
          });

  return (
    <View>
      <GroupedCard>
        <CareRow
          source={HISTORY_ILLUSTRATION}
          title={t("you.care_context.medical_history_label")}
          subtitle={t("you.care_context.medical_history_continue")}
          onPress={() => onNavigate({ screen: "MedicalHistory" })}
        />
        <CareRow
          source={ICONS.syringe}
          title={t("you.care_context.past_treatment_label")}
          subtitle={pastTreatmentLabel}
          divider
          onPress={() => onNavigate({ screen: "FertilityTreatmentsFlow" })}
        />
        <CareRow
          source={CRYO_ILLUSTRATION}
          title={t("you.care_context.cryo_inventory_label")}
          subtitle={cryoLabel}
          divider
          onPress={() => onNavigate({ screen: "CryoVault" })}
        />
        <CareRow
          source={ICONS.envelope}
          title={t("you.care_context.records_label")}
          subtitle={recordsLabel}
          divider
          onPress={() =>
            onNavigate({
              screen: "Documents",
              params: {
                initialCategory: "medical_records",
                initialDocumentFilterLabel: t(
                  "you.care_context.records_filter_label",
                ),
              },
            })
          }
        />
      </GroupedCard>
    </View>
  );
}
